// tables/DefaultTable.tsx
import React from 'react';
import { Card } from '@components/card/card';

interface DefaultTableProps {
  data: any[];
  selectedId: string | null;
  onSelect: (id: string, code: string) => void;
  isLoading: boolean;
  language: string;
  transactionType: string;
}

export const DefaultTable: React.FC<DefaultTableProps> = ({
  data,
  selectedId,
  onSelect,
  isLoading,
  language,
  transactionType
}) => {
  const isArabic = language === 'ar';

  const getTotalAmount = () => {
    return data.reduce((sum, item) => sum + (item.netAmount || item.grossAmount || 0), 0);
  };

  const getSelectedAmount = () => {
    const selectedItem = data.find(item => item.id === selectedId);
    return selectedItem ? (selectedItem.netAmount || selectedItem.grossAmount || 0) : 0;
  };

  const getTransactionTypeName = () => {
    switch (transactionType) {
      case 'SOI': return 'أوامر البيع';
      case 'SOP': return 'أوامر نقاط البيع';
      case 'BIL': return 'فواتير المشتريات';
      case 'SPR': return 'مرتجعات المشتريات';
      case 'SRP': return 'مرتجعات المشتريات من أمر';
      case 'RFG': return 'استلام منتج نهائي';
      default: return 'المراجع';
    }
  };

  const getBadgeColor = () => {
    switch (transactionType) {
      case 'SOI': return 'lime';
      case 'SOP': return 'orange';
      case 'BIL': return 'violet';
      case 'SPR': return 'teal';
      case 'SRP': return 'sky';
      case 'RFG': return 'success';
      default: return 'secondary';
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
        <p className="text-muted mt-2">جاري تحميل البيانات...</p>
      </div>
    );
  }

  return (
    <Card className="mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-table me-2"></i>
          قائمة {getTransactionTypeName()}
          <span className={`badge bg-${getBadgeColor()} ms-2`}>{getTransactionTypeName()}</span>
        </h6>
        <div>
          <span className="badge bg-theme me-2">
            {selectedId ? 'تم الاختيار' : 'لم يتم الاختيار'}
          </span>
          <span className="badge bg-success">
            الإجمالي: {getTotalAmount().toLocaleString()} ر.س
          </span>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light fw-bold">
              <tr>
                <th className="text-center small" style={{ width: '50px' }}>#</th>
                <th className="text-center small" style={{ width: '20%' }}>الرقم</th>
                <th className="text-center small" style={{ width: '20%' }}>التاريخ</th>
                <th className="text-center small" style={{ width: '30%' }}>الطرف</th>
                <th className="text-center small" style={{ width: '15%' }}>المبلغ</th>
                <th className="text-center small" style={{ width: '15%' }}>الوحدة</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const isSelected = selectedId === item.id;
                
                return (
                  <tr 
                    key={item.id} 
                    className={isSelected ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelect(item.id, item.code || item.name)}
                  >
                    <td className="text-center align-middle">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="defaultSelection"
                          checked={isSelected}
                          onChange={() => onSelect(item.id, item.code || item.name)}
                        />
                      </div>
                    </td>
                    
                    <td className="text-center align-middle fw-bold">
                      {item.code || item.name}
                    </td>
                    
                    <td className="text-center align-middle">
                      {item.transactionDate 
                        ? new Date(item.transactionDate).toLocaleDateString('ar-SA')
                        : '-'}
                    </td>
                    
                    <td className="text-center align-middle">
                      {isArabic ? (item.peopleName2 || item.peopleName) : (item.peopleName || item.peopleName2)}
                    </td>
                    
                    <td className="text-center align-middle">
                      <span className="fw-bold text-primary">
                        {(item.netAmount || item.grossAmount || 0).toLocaleString()} ر.س
                      </span>
                    </td>
                    
                    <td className="text-center align-middle">
                      {isArabic ? (item.organizationName2 || item.organizationName) : (item.organizationName || item.organizationName2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-footer d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          عرض {data.length} من {data.length} {getTransactionTypeName()}
        </div>
        <div className="text-theme fw-bold">
          {selectedId 
            ? `المبلغ المحدد: ${getSelectedAmount().toLocaleString()} ر.س`
            : `يرجى اختيار ${getTransactionTypeName()}`}
        </div>
      </div>
    </Card>
  );
};